import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';
import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

interface CopyToClipboardProps {
  children: React.ReactNode;
  textToCopy: string;
  alertMessageDuration?: number;
  hideTextWhileCopying?: boolean;
  sx?: SxProps<Theme>;
}

const CopyToClipboard = ({
  textToCopy,
  children,
  alertMessageDuration = 2000,
  hideTextWhileCopying = false,
  sx,
}: CopyToClipboardProps) => {
  const [error, setError] = useState<string | undefined>();
  const [showMessage, setShowMessageState] = useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const onLinkClick = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      setError(undefined);

      try {
        await copyToClipboard(textToCopy);
        setShowMessageState(true);
      } catch (error) {
        setError(`${t('approvals.copy_error')}: ${error}`);
      }
    },
    [textToCopy],
  );

  useEffect(() => {
    if (showMessage && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        setShowMessageState(false);
        timeoutRef.current = undefined;
      }, alertMessageDuration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [showMessage, alertMessageDuration]);

  const alertSx = {
    p: 0,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '& .MuiAlert-icon': { fontSize: '14px', mr: 0, p: 0 },
    '& .MuiAlert-message': { fontSize: '12px', p: 0 },
  };

  return (
    <ButtonBase
      onClick={onLinkClick}
      sx={{
        p: 0,
        backgroundColor: 'transparent',
        display: 'inline-flex',
        justifyContent: 'space-between',
        width: hideTextWhileCopying ? 'fit-content' : '100%',
        ...sx,
        '&:hover': { backgroundColor: 'transparent' },
      }}
      disableRipple
    >
      {hideTextWhileCopying && showMessage ? null : children}

      {error && (
        <Alert severity="error" sx={alertSx}>
          {error}
        </Alert>
      )}

      {!error && showMessage && (
        <Alert severity="success" sx={alertSx}>
          {t('approvals.copy_successful')}
        </Alert>
      )}
    </ButtonBase>
  );
};

export { CopyToClipboard };
